<template>
    <div class="package-card lead-form">
        <h3>Have a representative contact you</h3>
        <p class="sub">We have your contact information on file — just tell us how you'd like your representative to reach you. They will follow up within one business day.</p>

        <div class="has-text-grey mt-5" v-if="loading"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading your information...</div>

        <div v-else>
            <div class="section-label mt-5">Your teaching details</div>
            <p class="section-sub">Accurate district and salary-schedule details help your representative size the right bundle before they call.</p>
            <div class="columns mt-2 mb-0">
                <div class="column">
                    <b-field label="District" label-position="inside">
                        <b-select v-model="district_id" expanded>
                            <option :value="0">Not Listed/Not Applicable</option>
                            <option :value="999999">Private School</option>
                            <option v-for="district in districts" :key="'district-' + district.id" :value="district.id">{{ district.name }} ({{ district.city | capitalize }})</option>
                        </b-select>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="School" label-position="inside">
                        <b-select v-model="school_id" expanded>
                            <option :value="0">Not Listed/Not Applicable</option>
                            <option v-for="school in schools" :key="'school-' + school.id" :value="school.id">{{ school.name }} ({{ school.city | capitalize }})</option>
                        </b-select>
                    </b-field>
                </div>
            </div>
            <div class="columns mb-0">
                <div class="column">
                    <b-field label="Highest degree level" label-position="inside">
                        <b-select v-model="meta.highest_degree" expanded placeholder="Select...">
                            <option v-for="option in degreeOptions" :key="'degree-' + option" :value="option">{{ option }}</option>
                        </b-select>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Current clock hour/credit column" label-position="inside">
                        <b-select v-model="meta.hours_column" expanded placeholder="Select...">
                            <!-- The district's actual columns when a schedule is
                                 on file; the standard ladder otherwise -->
                            <template v-if="scaleColumns">
                                <option v-for="option in scaleColumns" :key="'column-' + option.value" :value="option.value">{{ option.label }}</option>
                                <option value="This does not apply">This does not apply</option>
                            </template>
                            <template v-else>
                                <option v-for="option in hoursOptions" :key="'column-' + option" :value="option">{{ option }}</option>
                            </template>
                        </b-select>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Years of experience" label-position="inside">
                        <b-select v-model="meta.years_teaching" expanded placeholder="Select...">
                            <option v-for="option in yearsOptions" :key="'years-' + option" :value="option">{{ option }}</option>
                        </b-select>
                    </b-field>
                </div>
            </div>

            <!-- No published schedule for their district (or only an outdated
                 one): invite the source document (link or upload) so staff
                 can get it on file -->
            <div class="mt-2" v-if="hasRealDistrict && (!scale || scaleStale)">
                <b-field>
                    <template #label>
                        Your district's salary schedule or CBA <span class="optional">(optional)</span>
                    </template>
                    <b-input v-model="lead.cba_url" placeholder="Link to the document (https://...)"/>
                </b-field>
                <div class="cba-upload">
                    <b-upload v-model="cbaFile" :accept="cbaAccept" @input="checkCbaFile">
                        <a class="upload-link"><i class="far fa-paperclip mr-1"></i>Attach the document</a>
                    </b-upload>
                    <span class="file-chip" v-if="cbaFile">
                        <i class="far fa-file mr-1"></i>{{ cbaFile.name }}
                        <a class="file-clear" @click="cbaFile = null"><i class="fas fa-times"></i></a>
                    </span>
                </div>
                <p class="section-sub mt-1">{{ scale ? 'Our copy of ' + profile.district + '’s schedule is from a prior year.' : 'We don’t have ' + profile.district + '’s current schedule on file.' }} Sharing the source document — your Collective Bargaining Agreement (CBA) or salary schedule — helps your representative show you exactly what moving up is worth. A link and a copy of the file are both helpful, so share both if you can.</p>
            </div>

            <b-field label="Reach me by" class="mt-4">
                <div class="contact-options">
                    <button class="contact-option"
                            v-for="option in contactOptions"
                            :key="option.key"
                            :class="{'is-selected': isSelected(option.key)}"
                            @click="toggle(option.key)">
                        <i class="far fa-fw check" :class="isSelected(option.key) ? 'fa-check-square' : 'fa-square'"></i>
                        <i class="far fa-fw channel-icon" :class="option.icon"></i>
                        <span class="channel">{{ option.label }}</span>
                        <span class="value">{{ option.value }}</span>
                    </button>
                </div>
            </b-field>
            <p class="update-note">Need to update these? Edit your contact information in <router-link :to="{name: 'account'}">Your Account</router-link>, then come back.</p>

            <b-field label="Best time to reach you" class="mt-5">
                <div class="chips">
                    <button class="chip"
                            v-for="time in leadTimes"
                            :key="'time-' + time"
                            :class="{'is-selected': lead.time === time}"
                            @click="lead.time = lead.time === time ? null : time">{{ time }}</button>
                </div>
            </b-field>

            <b-field class="mt-4">
                <template #label>
                    What are you looking for? <span class="optional">(optional)</span>
                </template>
                <b-input v-model="lead.note" type="textarea" rows="3"
                         placeholder="e.g. I need 50 clock hours before June and teach middle school science."/>
            </b-field>

            <b-button type="is-primary" expanded size="is-medium" class="mt-4"
                      :disabled="!canSubmit || submitting" @click="submit">
                <i class="fas fa-spin fa-spinner mr-2" v-if="submitting"></i>Request My Representative
            </b-button>
            <b-button type="is-text" expanded class="mt-2" @click="$emit('back')">
                <i class="fas fa-arrow-left mr-2"></i>Back
            </b-button>
        </div>
    </div>
</template>

<script>
    import {mapGetters, mapMutations} from "vuex";
    import {client as http} from "@/http_client";
    import {LEAD_TIMES} from "@/constants/packages";
    import {DEGREE_OPTIONS, HOURS_COLUMN_OPTIONS, YEARS_TEACHING_OPTIONS} from "@/constants/profile";
    import {scaleColumnOptions, isStaleScale} from "./scaleMath";

    export default {
        name: "PackageLeadForm",
        data: function() {
            return {
                leadTimes: LEAD_TIMES,
                loading: true,
                submitting: false,
                contactOptions: [],
                selected: [],
                degreeOptions: DEGREE_OPTIONS,
                hoursOptions: HOURS_COLUMN_OPTIONS,
                yearsOptions: YEARS_TEACHING_OPTIONS,
                districts: [],
                schools: [],
                scale: null,
                district_id: undefined,
                school_id: undefined,
                initialSchoolId: null,
                profile: {},
                meta: {highest_degree: null, hours_column: null, years_teaching: null},
                saved: null,
                initializing: true,
                cbaFile: null,
                cbaAccept: '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp',
                lead: {
                    time: null,
                    note: '',
                    cba_url: ''
                }
            };
        },
        computed: {
            ...mapGetters(['pkgHours']),
            canSubmit() {
                return this.selected.length > 0;
            },
            hasRealDistrict() {
                return !!this.district_id && this.district_id !== 0 && this.district_id !== 999999;
            },
            scaleStale() {
                return isStaleScale(this.scale);
            },
            scaleColumns() {
                return scaleColumnOptions(this.scale, this.meta.highest_degree);
            },
            // Same dirty tracking as StepNeeds: the profile is only written
            // when the answers actually changed
            dirty() {
                if (!this.saved) return false;
                return this.saved.district_id !== this.district_id
                    || this.saved.school_id !== this.school_id
                    || this.saved.highest_degree !== this.meta.highest_degree
                    || this.saved.hours_column !== this.meta.hours_column
                    || this.saved.years_teaching !== this.meta.years_teaching;
            }
        },
        methods: {
            ...mapMutations(['setUser']),
            isSelected(key) {
                return this.selected.includes(key);
            },
            toggle(key) {
                const index = this.selected.indexOf(key);
                if (index === -1) {
                    this.selected.push(key);
                } else {
                    this.selected.splice(index, 1);
                }
            },
            // One option per contact point on file: each email is reachable
            // as email; each phone as a text or a voice call
            buildOptions(profile) {
                const options = [];
                for (const field of ['email', 'email_2']) {
                    if (!profile[field]) continue;
                    options.push({key: 'email:' + field, channel: 'email', field, icon: 'fa-envelope', label: 'Email', value: profile[field]});
                }
                for (const field of ['phone', 'phone_2']) {
                    if (!profile[field]) continue;
                    options.push({key: 'text:' + field, channel: 'text', field, icon: 'fa-comment-sms', label: 'Text', value: profile[field]});
                    options.push({key: 'call:' + field, channel: 'call', field, icon: 'fa-phone', label: 'Call', value: profile[field]});
                }
                this.contactOptions = options;
                // Primary email preselected as the low-friction default
                if (options.some(o => o.key === 'email:email')) this.selected = ['email:email'];
            },
            load() {
                http.get('app/users/profile-verification', {force: true}).then(response => {
                    this.profile = {...response.data.profile};
                    this.meta = {...this.meta, ...response.data.meta};
                    this.initialSchoolId = this.profile.school_id;
                    this.buildOptions(response.data.profile || {});
                    this.loadDistricts();
                }).catch(err => {
                    console.error(err);
                    this.loading = false;
                });
            },
            loadDistricts() {
                http.get('districts').then(results => {
                    this.districts = results.data.filter(d => d.id !== 9999999);
                    if (this.profile.district === 'Private School') {
                        this.district_id = 999999;
                    } else if (this.profile.district_id && this.districts.some(d => d.id === this.profile.district_id)) {
                        this.district_id = this.profile.district_id;
                    } else {
                        this.district_id = 0;
                    }
                    this.loading = false;
                    // Synthetic districts have no school lookup pending — the
                    // dirty baseline can settle as soon as the watchers run
                    if (this.district_id === 0 || this.district_id === 999999) {
                        this.$nextTick(() => this.snapshot());
                    }
                }).catch(err => {
                    console.error(err);
                    this.loading = false;
                });
            },
            loadScale(districtId) {
                this.scale = null;
                if (!districtId || districtId === 999999) return;
                http.get('districts/' + districtId + '/pay-scale').then(response => {
                    this.scale = response.data;
                }).catch(() => {
                    // 404 = no schedule on file; the standard ladder stands in
                });
            },
            snapshot() {
                this.saved = {
                    district_id: this.district_id,
                    school_id: this.school_id,
                    highest_degree: this.meta.highest_degree,
                    hours_column: this.meta.hours_column,
                    years_teaching: this.meta.years_teaching
                };
                this.initializing = false;
            },
            // Persist changed answers first (same endpoint and semantics as
            // the wizard's Your Needs step), then create the lead — so the
            // representative sees accurate district/scale details
            saveProfileIfDirty() {
                if (!this.dirty) return Promise.resolve();
                return http.post('app/users/profile-verification', {
                    district: this.profile.district,
                    district_id: (this.district_id === 0 || this.district_id === 999999) ? null : this.district_id,
                    school: this.profile.school,
                    school_id: (this.school_id === 0) ? null : this.school_id,
                    meta: {highest_degree: this.meta.highest_degree, hours_column: this.meta.hours_column, years_teaching: this.meta.years_teaching}
                }).then(() => {
                    this.setUser({...this.$store.getters.currentUser, district: this.profile.district, school: this.profile.school});
                    this.snapshot();
                });
            },
            checkCbaFile(file) {
                if (file && file.size > 25 * 1024 * 1024) {
                    this.cbaFile = null;
                    this.$notify('error', 'File Too Large', 'Please choose a file under 25 MB.', {duration: 8000, permanent: false});
                }
            },
            // Schedule sharing is its own submission (same endpoint as the
            // wizard's ask): it lands in the admin processing queue, and a
            // failure never blocks the contact request itself
            shareSchedule() {
                const url = this.lead.cba_url.trim();
                if (!this.hasRealDistrict || (this.scale && !this.scaleStale) || (!url && !this.cbaFile)) return Promise.resolve();
                const form = new FormData();
                if (url) form.append('url', url);
                form.append('district_id', this.district_id);
                if (this.cbaFile) form.append('file', this.cbaFile);
                return http.post('app/users/cba-link', form).catch(err => {
                    console.error('Could not share salary schedule:', err);
                });
            },
            submit() {
                if (!this.canSubmit || this.submitting) return;
                this.submitting = true;

                const contacts = this.selected
                    .map(key => this.contactOptions.find(o => o.key === key))
                    .filter(Boolean)
                    .map(o => ({channel: o.channel, field: o.field}));

                const payload = {contacts, best_time: this.lead.time, note: this.lead.note};
                if (this.pkgHours) payload.bundle = {hours: this.pkgHours};

                this.saveProfileIfDirty().catch(err => {
                    // A failed profile save shouldn't block the contact request
                    console.error('Could not save profile before lead:', err);
                }).then(() => {
                    return this.shareSchedule();
                }).then(() => {
                    return http.post('app/users/bundle-lead', payload);
                }).then(() => {
                    this.submitting = false;
                    this.$emit('submitted');
                }).catch(err => {
                    console.error(err);
                    this.submitting = false;
                    const serverMsg = err.response && err.response.data && err.response.data.error;
                    this.$notify('error', 'Could Not Submit', serverMsg || 'Something went wrong sending your request. Please try again, or contact support@my247ed.com.', {duration: 8000, permanent: false});
                });
            }
        },
        watch: {
            district_id: function(val) {
                this.schools = [];
                this.loadScale(val);
                if (val === 0) {
                    this.profile.district = 'Not Listed/Not Applicable';
                } else if (val === 999999) {
                    this.profile.district = 'Private School';
                } else {
                    let code;
                    this.districts.forEach(d => {
                        if (d.id === val) {
                            this.profile.district = d.name;
                            code = d.code;
                        }
                    });
                    if (code) {
                        http.get('districts/schoolsIn/' + code).then(results => {
                            this.schools = results.data;
                            if (this.initialSchoolId && this.schools.some(s => s.id === this.initialSchoolId)) {
                                this.school_id = this.initialSchoolId;
                            }
                            this.initialSchoolId = null;
                            if (this.initializing) this.$nextTick(() => this.snapshot());
                        }).catch(err => {
                            console.error(err);
                            if (this.initializing) this.snapshot();
                        });
                    } else if (this.initializing) {
                        this.$nextTick(() => this.snapshot());
                    }
                }
                if (val === 0 || val === 999999) {
                    this.school_id = 0;
                    this.initialSchoolId = null;
                } else if (!this.initialSchoolId) {
                    this.school_id = undefined;
                }
            },
            school_id: function(val) {
                if (val === 0) {
                    this.profile.school = 'Not Listed/Not Applicable';
                } else {
                    this.schools.forEach(s => {
                        if (s.id === val) this.profile.school = s.name;
                    });
                }
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .lead-form {
        max-width: 680px;
        margin: 2rem auto 0;

        h3 {
            font-size: 1.3rem;
            font-weight: 900;
            color: #33475b;
        }

        .sub {
            font-size: 0.9rem;
            color: #666d7a;
            margin-top: 0.5rem;
        }

        .section-label {
            font-size: 0.9rem;
            font-weight: bold;
            color: #33475b;
        }

        .section-sub {
            font-size: 0.8rem;
            color: #7a8a9d;
            margin-top: 2px;
        }

        .optional {
            font-weight: normal;
            color: #7a8a9d;
        }

        .contact-options {
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;

            .contact-option {
                font-family: inherit;
                display: flex;
                align-items: center;
                gap: 10px;
                width: 100%;
                text-align: left;
                font-size: 0.9rem;
                padding: 10px 14px;
                border-radius: 6px;
                cursor: pointer;
                border: 1px solid #c4cfda;
                background: #ffffff;
                color: #33475b;

                &:hover {
                    border-color: #7fa8cd;
                }

                &.is-selected {
                    border-color: #2e5f8f;
                    background: #f2f7fc;

                    .check {
                        color: #2e5f8f;
                    }
                }

                .check {
                    font-size: 1rem;
                    color: #7a8a9d;
                }

                .channel-icon {
                    color: #666d7a;
                }

                .channel {
                    font-weight: bold;
                    width: 3.2rem;
                    flex-shrink: 0;
                }

                .value {
                    color: #666d7a;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
            }
        }

        .update-note {
            font-size: 0.8rem;
            color: #7a8a9d;
            margin-top: 0.5rem;
        }

        .cba-upload {
            display: flex;
            align-items: center;
            gap: 0.7rem;
            flex-wrap: wrap;

            .upload-link {
                font-size: 0.8rem;
                color: #2e5f8f;
                text-decoration: underline;
            }

            .file-chip {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                background: #f2f7fc;
                border: 1px solid #dbe5ee;
                border-radius: 999px;
                padding: 2px 10px;
                font-size: 0.78rem;
                color: #33475b;
                max-width: 100%;
                overflow: hidden;

                .file-clear {
                    color: #7a8a9d;
                    margin-left: 2px;

                    &:hover {
                        color: #c0392b;
                    }
                }
            }
        }
    }
</style>
